import { Form, FormProps, Input, Modal, ModalProps, Typography } from 'antd';
import React, { useCallback } from 'react';
import { ButtonListFooterModalSM } from '../components/buttonsList';
import { ICompany } from '../../interfaces/companies';
import {
  companyAddress1Rules,
  companyAddress2Rules,
  companyCityRules,
  companyCountryRules,
  companyDirectorsFirstNameRules,
  companyDirectorsLastNameRules,
  companyEmailRules,
  companyFullNameRules,
  companyNameRules,
  companyPostalCodeRules,
  companyRegistrationNumberRules,
  companyStateProvinceRules,
  companyTelRules,
  companyTinRules,
} from './addCompany.constants';
import SingleLineTextArea from '../../components/pages/payment-request/singleLineTextArea';
import { confirmModalUnsavedChangesProps } from '../components/constants';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import { clearFieldErrorMessageOnChange, trimFormFieldValue, websiteRules } from '../../utils/form.constants';
import { AutoComplete } from '../../components/inputs/autocomplete';
import { DownOutlined } from '@ant-design/icons';

type Props = {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: any) => void;
  close: () => void;
  changeCompanyData: (data: ICompany) => void;
};

export const NewCompanyModalCreate = ({
  close,
  modalProps,
  formProps,
  formIsLoading,
  formSubmit,
  changeCompanyData,
}: Props) => {
  const onSubmit = useCallback(
    (formValues: ICompany) => {
      changeCompanyData({
        name: formValues.name,
        fullName: formValues.fullName?.length ? formValues.fullName : undefined,
        directorFirstName: formValues.directorFirstName,
        directorLastName: formValues.directorLastName,
        country: formValues.country,
        stateProvince: formValues.stateProvince,
        postalCode: formValues.postalCode,
        city: formValues.city,
        address1: formValues.address1,
        address2: formValues.address2?.length ? formValues.address2 : undefined,
        tinVatUid: formValues.tinVatUid?.length ? formValues.tinVatUid : undefined,
        regNo: formValues.regNo?.length ? formValues.regNo : undefined,
        phoneNumber1: formValues.phoneNumber1?.length ? formValues.phoneNumber1 : undefined,
        email: formValues.email?.length ? formValues.email : undefined,
        website: formValues.website?.length ? formValues.website : undefined,
      });
      formSubmit({});
    },
    [formSubmit, changeCompanyData],
  );

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: close,
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(elm => elm !== undefined);
    if (formIsNotEmpty) {
      openNotification();
    } else {
      close();
    }
  }, [close, formProps.form, openNotification]);

  return (
    <Modal
      {...modalProps}
      title='Add new provider'
      className='smartModalSM'
      afterClose={formProps.form?.resetFields}
      onCancel={onModalCancel}
      footer={ButtonListFooterModalSM(onSave, onModalCancel, formIsLoading)}
    >
      {contextHolder}
      <Form
        {...formProps}
        layout='vertical'
        onFinish={onSubmit}
        disabled={formIsLoading}
        onValuesChange={clearFieldErrorMessageOnChange(formProps.form)}
      >
        <Form.Item name='name' rules={companyNameRules}>
          <SingleLineTextArea placeholder='Provider Name' />
        </Form.Item>
        <Form.Item name='fullName' rules={companyFullNameRules}>
          <SingleLineTextArea placeholder='Full Provider Name' />
        </Form.Item>
        <Form.Item name='directorFirstName' rules={companyDirectorsFirstNameRules}>
          <SingleLineTextArea placeholder={`Director's first name`} />
        </Form.Item>
        <Form.Item name='directorLastName' rules={companyDirectorsLastNameRules}>
          <SingleLineTextArea placeholder={`Director's last name`} />
        </Form.Item>
        <Form.Item name='country' rules={companyCountryRules}>
          <AutoComplete
            resource='api/v1/worldCheck/countries'
            mapItem={(item: { value: string; label: string }) => ({
              value: item.label,
              key: item.value,
              label: <Typography.Text>{item.label}</Typography.Text>,
              data: item,
            })}
            notFoundPlaceholder='Start by typing country name'
          >
            <Input
              // style={{ fontSize: 16 }}
              placeholder='Country of incorporation'
              suffix={<DownOutlined />}
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item name='address1' rules={companyAddress1Rules}>
          <SingleLineTextArea placeholder='Address 1' />
        </Form.Item>
        <Form.Item name='address2' rules={companyAddress2Rules}>
          <SingleLineTextArea placeholder='Address 2' />
        </Form.Item>
        <Form.Item name='city' rules={companyCityRules}>
          <SingleLineTextArea placeholder={`City`} />
        </Form.Item>
        <Form.Item name='stateProvince' rules={companyStateProvinceRules}>
          <SingleLineTextArea placeholder={`State / Province`} />
        </Form.Item>
        <Form.Item name='postalCode' rules={companyPostalCodeRules}>
          <SingleLineTextArea placeholder={`Postal Code`} />
        </Form.Item>
        <Form.Item name='tinVatUid' rules={companyTinRules}>
          <Input placeholder='TIN' maxLength={20} />
        </Form.Item>
        <Form.Item name='regNo' rules={companyRegistrationNumberRules}>
          <Input placeholder='Reg &#8470;' maxLength={20} />
        </Form.Item>
        <Form.Item name='phoneNumber1' rules={companyTelRules}>
          <Input placeholder='Tel' />
        </Form.Item>
        <Form.Item name='email' rules={companyEmailRules}>
          <Input placeholder='Email' maxLength={256} />
        </Form.Item>
        <Form.Item name='website' rules={websiteRules}>
          <Input placeholder='Website' maxLength={256} onBlur={trimFormFieldValue('website', formProps.form)} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
